import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { PROJECT_KEY_MAX, PROJECT_NAME_MAX, type ErrorCode, type Project } from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import { useProjectCreate } from '../api/queries.js';
import styles from './CreateSectionModal.module.css';

/**
 * Новый раздел — то есть новый проект Jira.
 *
 * Создать его может только администратор Jira: скоуп приложения права человека не заменяет.
 * Проверять это заранее нечем — отдельного запроса о правах Jisy не делает, — поэтому форма
 * открыта всем, а отказ объясняется текстом на месте.
 */
export function CreateSectionModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (project: Project) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const create = useProjectCreate();

  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  /** Пока ключ не трогали руками, он идёт следом за названием. */
  const [keyEdited, setKeyEdited] = useState(false);

  const failure = create.error instanceof ApiRequestError ? create.error : null;
  const valid = name.trim().length > 0 && /^[A-Z][A-Z0-9]*$/u.test(key) && key.length >= 2;

  function submit(): void {
    if (!valid || create.isPending) return;
    create.mutate({ name: name.trim(), key }, { onSuccess: onCreated });
  }

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.modal} aria-describedby={undefined}>
          <Dialog.Title className={styles.title}>{t('sections.createTitle')}</Dialog.Title>

          <label className={styles.field}>
            <span className="label">{t('sections.createName')}</span>
            <input
              className={styles.input}
              value={name}
              maxLength={PROJECT_NAME_MAX}
              autoFocus
              onChange={(event) => {
                setName(event.target.value);
                if (!keyEdited) setKey(keyFromName(event.target.value));
              }}
              onKeyDown={(event) => event.key === 'Enter' && submit()}
              placeholder={t('sections.createNamePlaceholder')}
            />
          </label>

          <label className={styles.field}>
            <span className="label">{t('sections.createKey')}</span>
            <input
              className={`mono ${styles.input} ${styles.inputKey}`}
              value={key}
              maxLength={PROJECT_KEY_MAX}
              onChange={(event) => {
                setKeyEdited(true);
                // Jira принимает только заглавную латиницу и цифры — приводим сразу, а не отказом.
                setKey(event.target.value.toUpperCase().replace(/[^A-Z0-9]/gu, ''));
              }}
              onKeyDown={(event) => event.key === 'Enter' && submit()}
            />
            <span className={styles.hint}>{t('sections.createKeyHint')}</span>
          </label>

          <p className={styles.note}>{t('sections.createNote')}</p>

          {failure && (
            <p className={styles.error} role="alert">
              {t(`errors.${failure.code}` satisfies `errors.${ErrorCode}`, failure.params ?? {})}
            </p>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primary}
              disabled={!valid || create.isPending}
              onClick={submit}
            >
              {create.isPending ? t('sections.creating') : t('sections.createSubmit')}
            </button>
            <Dialog.Close asChild>
              <button type="button" className={styles.secondary}>
                {t('issue.cancel')}
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Кириллица в ключ не годится, поэтому названия транслитерируются по одной букве. */
const TRANSLIT: Record<string, string> = {
  а: 'A', б: 'B', в: 'V', г: 'G', д: 'D', е: 'E', ё: 'E', ж: 'ZH', з: 'Z', и: 'I',
  й: 'Y', к: 'K', л: 'L', м: 'M', н: 'N', о: 'O', п: 'P', р: 'R', с: 'S', т: 'T',
  у: 'U', ф: 'F', х: 'H', ц: 'C', ч: 'CH', ш: 'SH', щ: 'SCH', ъ: '', ы: 'Y', ь: '',
  э: 'E', ю: 'YU', я: 'YA',
};

/**
 * Ключ по названию — как это делает сама Jira: из первых букв слов, а для одного слова —
 * из его начала. Значение только предлагается: поле рядом, и поправить его можно до отправки.
 */
function keyFromName(name: string): string {
  const words = name
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .map((word) => [...word].map((letter) => TRANSLIT[letter] ?? letter).join(''))
    // Регистр здесь смешанный: транслитерация уже вернула заглавные, а латиница осталась строчной.
    .map((word) => word.replace(/[^a-zA-Z0-9]/gu, ''))
    .filter(Boolean);

  if (words.length === 0) return '';

  const candidate =
    words.length > 1
      ? words.map((word) => word.charAt(0)).join('')
      : (words[0] ?? '').slice(0, 4);

  // Ключ обязан начинаться с буквы: цифры в начале Jira не примет.
  return candidate.toUpperCase().replace(/^[0-9]+/u, '').slice(0, PROJECT_KEY_MAX);
}
